import { Button, Divider, Input, Modal, Select, Table, Tooltip } from "antd";
import API from "API";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FileProtectOutlined, FormOutlined, SaveOutlined, TableOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { colorSet } from "const";
import StudentDashboard from "../StudentDashboard";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { correct_media, downloadFile, searchWithoutAccent } from "general_function";

const NOW = dayjs();

function getColor(score) {
    if (score < 0) return;
    if (score < 0.5) return colorSet["low"];
    if (score < 0.8) return colorSet["medium"];
    return colorSet["high"]
}

function Cell({ courses, id }) {
    const course = useMemo(() => courses.find(item => id === item.course_id),[courses ,id])
    const final_result = useMemo(() => 
        course?.scores?.reduce((sum, item) => sum + (Math.max(item.score, 0)*item.rate/100), 0)
    ,[course])

    return course ? <>
        <Tooltip title="Trắc nghiệm đã làm">
            <span style={{ color: getColor((course.total_correct + course.total_wrong) / course.total_quizzes) }}>
                <UnorderedListOutlined />: {course.total_correct + course.total_wrong}/{course.total_quizzes}
            </span>
        </Tooltip><br />
        <Tooltip title="Tự luận đã làm">
            <span style={{ color: getColor(course.total_essays_done / course.total_essays) }}>
                <FormOutlined />: {course.total_essays_done}/{course.total_essays}
            </span>
        </Tooltip>
        <Divider style={{ marginTop: 3, marginBottom: 3 }}/>
        {course.scores?.map(item => <React.Fragment key={item.id}>
            <Tooltip title={`${item.title} - ${item.rate}%`}>
                <span style={{ color: item.score === -1 && item.end_time !== null && dayjs(item.end_time).isBefore(NOW)
                    ? colorSet["low"] : getColor(item.score/1000) }}>
                    <FileProtectOutlined />: {item.score === -1 ? (
                    item.end_time === null ? <span>Chưa nhập điểm</span> :
                    dayjs(item.end_time).isBefore(NOW) ? <span style={{ color: colorSet["low"] }}>Không làm bài</span> :
                    dayjs(item.start_time).isAfter(NOW) ? <span>Chưa đến giờ</span> :
                    <span>Chưa làm bài</span>
                    ) : item.score/100}
                </span>
            </Tooltip><br />
        </React.Fragment>)}
        {course.scores && <><Divider style={{ marginTop: 3, marginBottom: 3 }}/>
        <Tooltip title="Điểm tổng kết">
            <span style={{ color: getColor(final_result/1000) }}>
                <TableOutlined />: {final_result/100}
            </span>
        </Tooltip></>}
    </> : <>Không học trong kỳ</>
}

export default function ExerciseDashboard({ selectedTerm }) {
    const [gettingData, setGettingData] = useState(true);
    const [students, setStudents] = useState({
        page: 1,
        results: [],
        count: 0,
    });
    const [columns, setColumns] = useState([]);
    const [columnOptions, setColumnOptions] = useState([]);
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [search, setSearch] = useState("");
    const [selectedUser, setSelectedUser] = useState(null);
    const [downloading, setDownloading] = useState(false);
    useEffect(() => {
        setGettingData(true);
        API.post("/dashboard-statistics/", {
            term_id: selectedTerm.id,
            page: students.page,
            name: search,
        })
            .then(res => {
                setColumns([
                    {
                        title: "Sinh viên",
                        key: "name",
                        minWidth: 200,
                        render: value => <Link to={`/users/${value.id}`}>
                            {value.last_name} {value.first_name}
                        </Link>
                    },
                    ...res.data.columns.map(item => ({
                        title: item.name,
                        key: item.id,
                        dataIndex: "courses",
                        minWidth: 160,
                        render: value => <Cell courses={value} id={item.id}/>
                    }))])
                setColumnOptions(res.data.columns.map(item => ({
                    label: item.name,
                    value: item.id
                })))
                setStudents({
                    page: students.page,
                    results: res.data.results,
                    count: res.data.count,
                })
                setGettingData(false);
            })
            .catch(error => {
                console.log(error);
            })
    }, [selectedTerm.id, students.page, search])

    const filterColumns = useMemo(() => selectedColumns.length === 0 ? columns
        : columns.filter(item => item.key === "name" || selectedColumns.includes(item.key))
        , [selectedColumns, columns])

    const handleDownload = useCallback(() => {
        setDownloading(true);
        API.post("/dashboard-statistics/", {
            term_id: selectedTerm.id,
            name: search,
            export: true,
            courses: selectedColumns,
        })
        .then(res => {
            const path = correct_media(res.data);
            downloadFile(path);
        })
        .catch(error => {

        })
        .finally(() => {
            setDownloading(false);
        })
    },[search, selectedColumns, selectedTerm.id])

    return <>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Input.Search
                placeholder='Tìm sinh viên'
                onSearch={setSearch}
                style={{ width: 400 }}
                disabled={gettingData}
                allowClear
            />
            <Button type="primary" loading={downloading} disabled={downloading} onClick={handleDownload}>
                Tải file <SaveOutlined />
            </Button>
        </div>
        <Select
            placeholder="Chọn cột hiển thị"
            options={columnOptions}
            style={{ width: "100%", marginTop: 5 }}
            mode="multiple"
            value={selectedColumns}
            onChange={setSelectedColumns}
            filterOption={searchWithoutAccent}
        />
        <div style={{ overflowX: "auto" }}>
            <Table
                rowKey={"id"}
                columns={filterColumns}
                dataSource={students.results}
                loading={gettingData}
                pagination={{
                    current: students.page,
                    pageSize: 10,
                    showSizeChanger: false,
                    onChange: (page) => setStudents({ ...students, page: page }),
                    total: students.count,
                    position: ["bottomLeft"],
                }}
                rowClassName="cursor-pointer"
                onRow={(record) => ({
                    onClick: () => {
                        setSelectedUser(record);
                    },
                })}
            />
        </div>
        <Modal
            open={selectedUser !== null}
            onCancel={() => setSelectedUser(null)}
            footer={[
                <Button key="close" onClick={() => setSelectedUser(null)} type="primary">Đóng</Button>
            ]}
            width={1350}
            title={`${selectedUser?.last_name} ${selectedUser?.first_name}`}
        >
            {selectedTerm.title} <br/>
            {selectedUser && <StudentDashboard selectedTerm={selectedTerm} user={selectedUser.id} />}
        </Modal>
    </>
}
